import * as THREE from "three";
import {PlanetAtmosphere} from "./planetAtmosphere.js";
import {Lights} from "../lights.js";
import {Globals} from "../../../global/globals.js";

/**
 * A planet
 */
export class Planet {
    static ROUGHNESS = .7;
    static METALNESS = 0;
    static DETAIL_LATITUDE = 64;
    static DETAIL_LONGITUDE = Planet.DETAIL_LATITUDE >> 1;
    static LIGHT_STRENGTH = Lights.SUN_STRENGTH_MAIN;

    /**
     * Construct a planet
     * @param {Vector3} nearest A vector from the camera to the nearest surface
     * @param {number} radius The planet radius
     * @param {PlanetSurface} surface The planet surface
     * @param {string | null} model The planet model
     * @param {Random} random A randomizer
     * @param {number} rotation The planet rotation speed
     * @param {number} tilt The planet tilt
     * @param {number} [atmosphereThickness] The atmosphere thickness, 0 if no atmosphere
     */
    constructor(
        nearest,
        radius,
        surface,
        model,
        random,
        rotation,
        tilt,
        atmosphereThickness = 0) {
        const totalRadius = radius * (1 + atmosphereThickness);

        this.origin = nearest.clone().normalize().multiplyScalar(totalRadius + nearest.length());
        this.scene = new THREE.Scene();
        this.lightScene = new THREE.Scene();
        this.rotation = 0;
        this.rotationSpeed = rotation;
        this.tilt = tilt;
        this.mesh = null;

        if (model) {
            this.mesh = new THREE.Scene();

            Globals.MODEL_CACHE.load(model).then(mesh => {
                this.mesh.add(mesh);
            });
        }
        else {
            const geometry = new THREE.SphereGeometry(radius, Planet.DETAIL_LATITUDE, Planet.DETAIL_LONGITUDE);
            const material = new THREE.MeshStandardMaterial({
                map: surface.texture,
                roughness: Planet.ROUGHNESS,
                metalness: Planet.METALNESS
            });

            this.mesh = new THREE.Mesh(geometry, material);

            if (atmosphereThickness !== 0) {
                const atmosphere = new PlanetAtmosphere(atmosphereThickness, surface.color.getHex()).makeMesh(geometry);

                atmosphere.position.copy(this.origin);

                this.scene.add(atmosphere);
            }

            const reflection = new THREE.DirectionalLight(surface.color, Planet.LIGHT_STRENGTH);

            reflection.position.copy(this.origin);

            this.lightScene.add(reflection);
        }

        this.mesh.position.copy(this.origin);

        this.scene.add(this.mesh);
    }

    /**
     * Update the state
     */
    update() {
        if ((this.rotation += this.rotationSpeed) > Math.PI * 2)
            this.rotation -= Math.PI * 2;
        else if (this.rotation < 0)
            this.rotation += Math.PI * 2;
    }

    /**
     * Update before rendering
     * @param {number} time The time interpolation in the range [0, 1]
     */
    render(time) {
        this.mesh.rotation.y = this.rotation + time * this.rotationSpeed;
        this.mesh.rotation.x = this.tilt;
    }
}